// 事件核查数据 —— 辨真社
// verdict 取值: verified(实锤·可信) | uncertain(待考证·存疑) | debunked(已辟谣·不实)

export type Verdict = "verified" | "uncertain" | "debunked";

export interface Category {
  key: string;
  name: string;
  desc: string;
}

export interface EvidenceItem {
  kind: string;
  text: string;
}

export interface FactArticle {
  slug: string;
  title: string;
  category: string;
  verdict: Verdict;
  date: string;
  readMinutes: number;
  heat: number;
  cover: string;
  claim: string;
  summary: string;
  opening: string;
  checks: EvidenceItem[];
  angles: string[];
  conclusion: string;
}

export const categories: Category[] = [
  { key: "hot", name: "社会热点", desc: "民生、公共事件与网络热议传言" },
  { key: "gossip", name: "吃瓜娱乐", desc: "明星、网红与粉丝圈相关瓜料" },
  { key: "ecommerce", name: "电商带货", desc: "直播间、种草帖与货不对板核查" },
  { key: "workplace", name: "职场内幕", desc: "大厂裁员、待遇与内部传闻" },
  { key: "tech", name: "科技圈", desc: "游戏、数码与互联网平台争议" },
];

export const categoryName = (key: string): string =>
  categories.find((c) => c.key === key)?.name ?? key;

export const verdictMeta: Record<
  Verdict,
  { label: string; short: string; tone: string }
> = {
  verified: {
    label: "已实锤 · 可信",
    short: "实锤",
    tone: "bg-accent-100 text-accent-800 border-accent-200",
  },
  uncertain: {
    label: "待考证 · 存疑",
    short: "存疑",
    tone: "bg-secondary-100 text-secondary-900 border-secondary-200",
  },
  debunked: {
    label: "已辟谣 · 不实",
    short: "不实",
    tone: "bg-primary-100 text-primary-800 border-primary-200",
  },
};

export const facts: FactArticle[] = [
  {
    slug: "youtaidian-miansha-rugged",
    title: "“白牌面膜敷出水光肌”系直播功效夸大，还是确有其效？",
    category: "ecommerce",
    verdict: "uncertain",
    date: "2026.09.05",
    readMinutes: 6,
    heat: 98210,
    cover: "https://readdy.ai/api/search-image?query=Minimalist%20editorial%20still%20life%20of%20a%20single%20white%20skincare%20jar%20on%20a%20warm%20sand%20colored%20studio%20table%2C%20soft%20natural%20window%20light%2C%20clean%20neutral%20background%2C%20no%20text%2C%20modern%20cosmetic%20advertising%20photography%2C%20airy%20and%20calm%20composition&width=1200&height=800&seq=fact-mask-2026&orientation=landscape",
    claim: "某头部主播在直播间宣称一款白牌面膜连续使用一周即可“敷出水光肌、淡退痘印”，并晒出前后对比照。",
    summary: "对比照缺少拍摄参数与第三方见证，功效宣称来自商家口径。目前证据不足以证伪，也无法独立证实。",
    opening:
      "一段反复循环的直播切片在多个平台被二次剪辑传播，画面里主播反复强调“七天见效、无效包退”。切片只有十几秒，前后对比照切换极快，几乎看不清皮肤状态与光线环境是否一致，这为后续核查留下了很大空间。",
    checks: [
      { kind: "溯源", text: "对比照可追溯到同一位化妆师的多场直播，疑似使用固定拍摄机位与滤镜预设。" },
      { kind: "成分", text: "产品备案成分表中以保湿剂和成膜剂为主，未见常见的强效焕肤成分，宣称“淡痘印”缺乏成分学支撑。" },
      { kind: "第三方", text: "未检索到公开的第三方检测报告，商家所称“实验室数据”无下载入口，难以交叉验证。" },
    ],
    angles: [
      "从传播学看，此类“七天见效”话术有鲜明的下单诱导结构：先制造焦虑，再给出一张无需验证的前后对比图，最后用“无效包退”降低决策门槛。真正的关键是，前后对比是否可复现。",
      "从成分学看，保湿与提亮、淡化瑕疵是两回事。成膜剂带来的即时“水润”观感属于物理遮盖，不等于皮肤状态的真实改善，这类区分普通消费者很难察觉。",
      "值得注意的反方证据：部分差评截图确实存在，但同样缺少下单与使用证据链。在网络争议里，任何单一截图都只能作为起点，不能作为结论。",
    ],
    conclusion:
      "综合来看，该功效宣称“暂时无法证实，亦无法完全证伪”。在缺乏第三方报告与可复现使用记录前，我们建议将其归为待考证，消费者下单前应保持审慎。",
  },
  {
    slug: "chayin-wushui-bingkuai",
    title: "网传连锁茶饮门店“污水冰块”视频，是摆拍还是实拍？",
    category: "hot",
    verdict: "debunked",
    date: "2026.09.04",
    readMinutes: 5,
    heat: 156720,
    cover: "https://readdy.ai/api/search-image?query=Editorial%20photography%20of%20clear%20ice%20cubes%20in%20a%20clean%20stainless%20steel%20container%20with%20dripping%20water%20droplets%2C%20cool%20moody%20lighting%20on%20a%20dark%20background%2C%20no%20text%2C%20crisp%20and%20cinematic%2C%20high%20detail%20food%20industry%20scene&width=1200&height=800&seq=fact-ice-2026&orientation=landscape",
    claim: "一则短视频显示，某连锁茶饮品牌门店后厨有人用疑似污水制冰，视频在平台获数十万转发。",
    summary: "核查确认视频画面经过剪辑与方位拼接，原始画面实为清洗滤水桶，与制冰无关，已定性为不实摆拍。",
    opening:
      "视频只有短短几秒，画面昏暗，人物背对镜头，水槽里水色浑浊，配文直接写“XX 茶饮用污水做冰块”。账号资料显示其发布者自称“前员工”，但并未提供任何能对应门店的在职证明，这条关键信息从一开始就缺失。",
    checks: [
      { kind: "定位", text: "视频中水槽型号、墙面瓷砖与该品牌多个区域的公开后厨样张不一致。" },
      { kind: "原始画面", text: "平台同源检索发现，同机位原始视频水槽内为透明清水，浑浊感来自剪辑时叠加的调色与裁切。" },
      { kind: "主体回应", text: "市场监管部门现场抽检后发布通报，未见用污水制冰行为，未发现证照问题。" },
    ],
    angles: [
      "这条视频之所以能快速传播，在于它击中了消费者对餐饮卫生的普遍担忧。情绪越强、画面越“实锤”，转发越难被理性核对，这正是摆拍内容常见的传播路径。",
      "对比原始画面与成片可以发现，所谓“浑浊污水”其实是阴影 + 后期色温导致的视觉误差，水龙头出水方向也与剪辑后叙事不符。",
      "值得肯定的是，评论区有网友晒出同一门店的透明冰柜监控片段进行反驳，多信源对撞在辟谣中往往比单方声明更有说服力。",
    ],
    conclusion:
      "综合定位、原始画面与官方抽检三方证据，这条“污水冰块”视频为不实剪辑内容，结论为已辟谣。请勿继续扩散。",
  },
  {
    slug: "shouyou-chouka-gailu",
    title: "手游抽卡概率公告截图流出：官方曾承诺的概率与实际不符？",
    category: "tech",
    verdict: "verified",
    date: "2026.09.03",
    readMinutes: 7,
    heat: 120430,
    cover: "https://readdy.ai/api/search-image?query=Close%20up%20editorial%20shot%20of%20colorful%20gaming%20cards%20and%20dice%20scattered%20on%20a%20warm%20wooden%20desk%2C%20soft%20dramatic%20light%2C%20digital%20game%20theme%2C%20no%20text%2C%20cinematic%20and%20vibrant%20but%20muted%20tones&width=1200&height=800&seq=fact-gacha-2026&orientation=landscape",
    claim: "某热门手游被曝更新后实际出货概率低于公示数值，玩家晒出千抽统计表与官方页面截图对比。",
    summary: "多组玩家的千抽记录、数据抓取与官方后台公开数据相互印证，差异客观存在，属于部分实锤。",
    opening:
      "事件的起点是一张被反复转发的统计表：玩家连续 1000 次抽卡后，按公示概率应当出货的期望次数与实际次数有明显落差。随后有技术向玩家抓取客户端上报数据，发现返回的概率字段与公示数值不同，讨论迅速从玩家圈扩散到普通用户。",
    checks: [
      { kind: "抽样", text: "至少 7 组千抽以上样本的出货率均低于公示值约 1.2～1.8 个百分点，样本量足以排除偶然波动。" },
      { kind: "抓包", text: "技术玩家公开了客户端概率字段与版本号，多位开发者复核后未发现篡改痕迹。" },
      { kind: "官方", text: "厂商发布说明承认“展示数值存在表述口径问题”，并承诺在下版本修正公示，变相印证了差异。" },
    ],
    angles: [
      "概率类争议最怕“样本不足”与“体感偏差”。本案可贵之处在于玩家用可复现的大样本和抓包数据把讨论从情绪拉回证据，是网络求证的正面范本。",
      "厂商的回应选择以“表述口径”定性，虽未直接承认造假，但修正公示本身说明此前数值与玩家体验确实不符，二者证据指向一致。",
      "此事也提醒玩家：抽卡是概率行为，判断“被坑”不要只靠几次体感，最好留存十连记录并汇总统计。",
    ],
    conclusion:
      "结合大样本统计、抓包数据与厂商修正承诺，该概率争议差异客观存在，我们将其归为部分实锤。相关细节仍有待官方完整公布。",
  },
  {
    slug: "dachang-caiyuan-mingdan",
    title: "“大厂裁员名单文档”在微信群疯传，内部流出还是网友拼凑？",
    category: "workplace",
    verdict: "uncertain",
    date: "2026.09.02",
    readMinutes: 6,
    heat: 88450,
    cover: "https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20of%20an%20empty%20modern%20office%20desk%20with%20a%20folded%20printed%20document%20and%20laptop%20closed%2C%20moody%20overcast%20natural%20light%2C%20neutral%20warm%20tone%2C%20no%20text%2C%20cinematic%20atmosphere&width=1200&height=800&seq=fact-layoff-2026&orientation=landscape",
    claim: "一份标注“某大厂 2026 秋季裁员名单”的文档截图在多个求职群与办公群传播，列有多部门及人员名单。",
    summary: "文档版式可被 Excel 样式编辑器伪造，名单信息含过时岗位与重复人员，真实性无法证实，也暂不能完全排除。",
    opening:
      "截图里的表格规整、带日期水印，看起来很像内部公告。但仔细看内容，名单中出现了已离职一年的岗位，还有同一个人出现两次的情况，这些细节与真实组织架构常理不符，成为核查的突破口。",
    checks: [
      { kind: "格式", text: "文档使用的字体与模板能通过在线表格工具一键生成，标题栏存在明显的“复制粘贴”残留样式。" },
      { kind: "名单", text: "多名被点名人员公开回应称“仍在职”，仅一人表示“近期有变动但与此表无关”。" },
      { kind: "主体", text: "该公司官方口径为“无批量裁员计划”，但未出具公章版声明，回应强度有限。" },
    ],
    angles: [
      "内部文档类爆料最容易因“看起来很正式”而获得信任。判断时优先看两点：一是能否与公开的组织架构对应，二是人员信息是否过时或重复——本案两点都出了问题。",
      "由于官方仅有口头否认、没有逐条辟谣，名单中仍有个别待核实条目，因此不能简单下“纯属造谣”的结论，只能定性为待考证。",
      "对求职者而言，与其轻信单张截图，不如直接向相关 HR 求证，或用企业公开的招聘渠道交叉确认，这比转发截图可靠得多。",
    ],
    conclusion:
      "该名单存在明显拼凑痕迹，但官方澄清力度不足、个别条目尚未落实，整体归为待考证，建议以官方正式渠道为准。",
  },
  {
    slug: "dingliu-nantuan-tongkuang",
    title: "顶流男团“恋情同框图”走红，原图追溯后竟是 AI 合成？",
    category: "gossip",
    verdict: "debunked",
    date: "2026.08.30",
    readMinutes: 4,
    heat: 211300,
    cover: "https://readdy.ai/api/search-image?query=Abstract%20cinematic%20visual%20of%20two%20silhouettes%20back%20to%20back%20separated%20by%20a%20soft%20blurred%20crowd%20and%20stage%20lights%2C%20dark%20concert%20background%2C%20moody%20and%20anonymous%2C%20no%20faces%20visible%2C%20no%20text&width=1200&height=800&seq=fact-tongkuang-2026&orientation=landscape",
    claim: "有账号晒出某顶流男团成员与一女子的同框图，称“实锤恋情”，话题冲上热搜。",
    summary: "原图经溯源为同人合成作品，画面中人物通过换脸技术拼接，原 Po 主已承认是练习作品。",
    opening:
      "这张所谓“同框图”光线自然、动作亲昵，乍看确实像偷拍。但放大后手部关节与耳廓边缘存在合成常见的柔边痕迹，背景人群里甚至出现了两张重复面孔，AI 生成的破绽相当明显。",
    checks: [
      { kind: "溯源", text: "以人物服装与站位做反向图片检索，可定位到早期同人站发布的合成练习图。" },
      { kind: "技术", text: "放大面部与手部后发现边缘抖动、耳廓重复纹理，符合当前常见换脸生成的典型缺陷。" },
      { kind: "原始", text: "原 Po 主随后删帖并承认“为练习合成的娱乐作品”，未涉及真人拍摄。" },
    ],
    angles: [
      "娱乐瓜料是 AI 合成内容的高发区，因为受众情绪强烈、辨识意愿低。看到“实锤”先别急着站队，放大画面查手、查耳、查重复元素是最快的初步筛查。",
      "真正的工作流是三步：先看图是否有合成痕迹，再做同物反向检索找到源头，最后回看发布者历史内容判断可信度。本案三步都指向合成。",
      "这类“娱乐向合成”被搬运后脱离创作语境，往往演变成传播事故，也再次说明“眼见”在网络时代早已不等于“为实”。",
    ],
    conclusion:
      "经溯源与技术核查，该同框图为 AI 合成练习作品，与真实恋情无关，结论为已辟谣。",
  },
  {
    slug: "zhubo-baitan-riwan",
    title: "网红晒“摆摊日入过万”账本，账目经得起对账吗？",
    category: "ecommerce",
    verdict: "debunked",
    date: "2026.08.27",
    readMinutes: 5,
    heat: 76540,
    cover: "https://readdy.ai/api/search-image?query=Warm%20evening%20street%20food%20stall%20scene%20with%20steaming%20food%20and%20warm%20string%20lights%2C%20a%20notebook%20ledger%20and%20coins%20on%20a%20wooden%20counter%20in%20foreground%2C%20no%20people%20faces%2C%20cozy%20documentary%20photography&width=1200&height=800&seq=fact-stall-2026&orientation=landscape",
    claim: "一名网红晒出摆摊一个月的收入账本截图，声称扣除成本后日入最高突破一万元，吸引大量网友“跟风摆摊”。",
    summary: "账本中的成本项缺失水电、摊位费与损耗，且与另一条视频的营收数字对不上，被指为引流话术。",
    opening:
      "账本截图列出的只有“营业额”和粗略的“毛利”，两列数字相加后被包装成“日入过万”。但细看会发现，真正决定是否赚钱的摊位租金、冷藏损耗、人工和食材报废都缺席了，这恰好是这类“致富账本”最常见的手法。",
    checks: [
      { kind: "对账", text: "同一账号另一期视频中自述的单品售价与账本单价不一致，前后数字难以互相印证。" },
      { kind: "成本", text: "账本未列出摊位费、水电、食材损耗等刚性成本，所谓“净赚”实为口径模糊的毛利。" },
      { kind: "轨迹", text: "该账号同期发布多条“教你摆摊”教程并开设付费社群，内容服务于引流变现。" },
    ],
    angles: [
      "“晒账本”是把抽象收入具象化的高效叙事，但账本只展示作者想让你看到的一列数字。对账的第一步，是把成本补全再算一次，多数“暴利”在此步即被戳穿。",
      "当一条内容既展示高收入、又紧接售卖教程与社群，其动机链已经相当完整，阅读时应把“晒账”与“变现”放在一起判断。",
      "对真想摆摊的人，与其被情绪化的日入数字带动，不如参考同类摊位的真实客流与均价做保守测算，赔率会更可控。",
    ],
    conclusion:
      "账本成本缺失且与同源视频自相矛盾，其目的偏向引流教学变现，结论为已辟谣的夸大营销内容。",
  },
  {
    slug: "zhugan-shuiguo-huoliang",
    title: "“助农直播水果个头缩水”争议：是批次问题还是货不对板？",
    category: "ecommerce",
    verdict: "verified",
    date: "2026.08.24",
    readMinutes: 6,
    heat: 54320,
    cover: "https://readdy.ai/api/search-image?query=Bright%20editorial%20still%20life%20of%20mixed%20oranges%20and%20apples%20in%20a%20plain%20cardboard%20fruit%20box%20on%20a%20light%20warm%20background%2C%20fresh%20produce%20packaging%2C%20no%20text%2C%20clean%20natural%20lighting&width=1200&height=800&seq=fact-fruit-2026&orientation=landscape",
    claim: "多名买家收到“助农直播”水果后反映个头明显小于宣传图，晒出开箱对比与尺寸标注。",
    summary: "宣传页标注的果径与多名买家实测不符，售后以“批次差异”回应但未提供规格证明，货不对板成立。",
    opening:
      "这条争议没有“反转”，而是买家们逐条贴出的实测证据：宣传页写果径 7cm 起，实际多个订单测量在 4.5～5.5cm 之间，附带的包装盒也没有任何规格标签。多方测量一致，让“批次差异”的解释难以站住脚。",
    checks: [
      { kind: "实测", text: "超过 30 位买家在评论区附上直尺测量图，果径分布高度一致，明显低于宣传下限。" },
      { kind: "宣传", text: "平台在售页面对果径、重量有明确文字承诺，且以红字突出“精选大果”。" },
      { kind: "售后", text: "客服统一话术为“受天气影响批次有差异”，但始终未提供本批次的产地分级证明。" },
    ],
    angles: [
      "助农直播自带情感背书，消费者下单时对“分量足”的期待往往被前置放大，一旦实物不符，落差感会比普通商品更强烈，也更愿意主动取证。",
      "判定货不对板的关键是“承诺是否可测”。本案中果径是可在页面量化的硬指标，买家用同一把尺子做出可复现测量，证据效力远高于主观感受。",
      "对商家而言，规格承诺一旦写在页面上就构成契约；天气因素可以作为免责话术，却无法替代可核查的分级标准。",
    ],
    conclusion:
      "买家实测、宣传承诺与售后回应三方对照，货不对板的事实成立，结论为实锤，建议通过平台售后渠道处理退款。",
  },
  {
    slug: "mingxing-tijian-baogao",
    title: "“明星体检报告泄露”截图流出，隐私是真泄漏还是营销噱头？",
    category: "gossip",
    verdict: "uncertain",
    date: "2026.08.20",
    readMinutes: 5,
    heat: 143600,
    cover: "https://readdy.ai/api/search-image?query=Abstract%20editorial%20image%20of%20a%20blurred%20medical%20report%20paper%20under%20a%20stethoscope%20with%20soft%20teal%20and%20warm%20overlay%20light%2C%20privacy%20and%20data%20security%20theme%2C%20no%20readable%20text%2C%20cinematic&width=1200&height=800&seq=fact-report-2026&orientation=landscape",
    claim: "有匿名账号发布疑似某明星的体检报告截图，称系“内部流出”，涉及多项个人健康信息。",
    summary: "截图关键信息被打码后难以核验真实身份，发布时机又与当事人新剧宣传高度重合，动机存疑。",
    opening:
      "这份“体检报告”的发布者从不出示能够证明截图来自医院的元数据，报告中的人名与日期也被打码，唯一完整保留的是能制造话题的“异常指标”。当一份所谓隐私文件只保留了能炒热话题的部分，它的真实性本身就值得打上问号。",
    checks: [
      { kind: "形式", text: "报告版式与多家医院公开样例一致，无法定位到具体医疗机构，存在套模板的可能。" },
      { kind: "动机", text: "发布节点恰逢当事人新剧定档，一周后官方澄清，但未对是否报案给出后续。" },
      { kind: "主体", text: "当事人工作室以“内容失实、涉嫌侵权”回应，但未提供该报告原件或第三方核验。" },
    ],
    angles: [
      "隐私类爆料的核查天然困难，因为“越真实越不该流传”，能完整流传的往往经过挑选与加工。遇到这种信息，第一条原则是：不做身份假设。",
      "判断动机比判断真伪更现实：谁在什么时间点、为什么让这份文件“恰好”流传？当曝光与作品宣发节奏重合，营销嫌疑会显著抬高。",
      "无论真伪，在未经证实时传播他人健康信息都涉嫌侵权。正确的做法是把线索交给当事人或平台方核验，而不是替爆料者站台。",
    ],
    conclusion:
      "报告真实性既无法证实也难以排除，且曝光动机存在明显营销嫌疑，整体归为待考证，建议谨慎对待、不扩散个人隐私内容。",
  },
  {
    slug: "chongdianbao-toudian",
    title: "充电宝品牌“后台偷电”聊天记录流出，数据能证明什么？",
    category: "tech",
    verdict: "uncertain",
    date: "2026.08.18",
    readMinutes: 6,
    heat: 62480,
    cover: "https://readdy.ai/api/search-image?query=Close%20up%20editorial%20shot%20of%20a%20black%20portable%20power%20bank%20charging%20a%20phone%20with%20a%20thin%20cable%20on%20a%20dark%20matte%20surface%2C%20electricity%20and%20battery%20theme%2C%20no%20text%2C%20moody%20lighting%20and%20clean%20composition&width=1200&height=800&seq=fact-battery-2026&orientation=landscape",
    claim: "一份疑似某充电宝厂商的内部群聊天记录称“可通过后台控制设备放电速度”，被解读为“偷电”。",
    summary: "聊天记录缺乏时间戳与上下文，未公开任何真实设备端的电量抓取数据，暂无实锤证据。",
    opening:
      "这段聊天记录没有完整头像、没有时间线，只有孤零零几句对话被红框圈出。它对“偷电”的指控，更多来自转发者的情绪解读，而不是记录本身的客观内容，核查一开始就缺少最基础的上下文。",
    checks: [
      { kind: "来源", text: "爆料账号无历史内容，聊天截图无法关联到具体人员或群组，唯一来源无法复核。" },
      { kind: "数据", text: "截至目前无任何第三方复现“后台操控放电”的实测抓包记录，仅有主观指控。" },
      { kind: "主体", text: "厂商否认并晒出软件版本更新日志，称“OTA 仅用于功能与安全补丁”。" },
    ],
    angles: [
      "聊天记录是最容易被断章取义的证据形态之一，因为缺少上下文，同一句话可以被解读成完全相反的意思。核查第一步永远是补齐“谁、何时、何群、前后文”。",
      "所谓“偷电”必须有可复现的设备端数据支撑，例如充电前后真实容量对比或抓包日志。目前只有一段无法溯源的对话，证据强度明显不足。",
      "在厂商已给出技术性解释的情况下，理性做法是等待有实测数据的一方提供证据，而不是在情绪驱使下先行定罪。",
    ],
    conclusion:
      "证据链条仅有无法溯源的聊天记录，缺乏设备实测数据，指控既未证实也未证伪，整体归为待考证。",
  },
];

export const featuredSlug = "dingliu-nantuan-tongkuang";
export const leadSlug = "chayin-wushui-bingkuai";

export const getArticle = (slug: string): FactArticle | undefined =>
  facts.find((f) => f.slug === slug);